import { createApi } from '@reduxjs/toolkit/query/react';
import type { FilterDTOType } from '../../../schemas';
import {
  type ImageModelListResponseType,
  ImageModelListResponseSchema,
  type OccasionCreateRequestType,
  type OccasionDTOType,
  type OccasionListResponseType,
  OccasionListResponseSchema,
  type StylingWorkflowCreateDTOType,
  type StylingWorkflowDTOType,
  type StylingWorkflowListResponseType,
  type StylingWorkflowRunItemDTOType,
} from '../../../schemas/styling';
import { stylistBaseQuery, stylistTagTypes } from './common';

type CreateStylingWorkflowPayload = StylingWorkflowCreateDTOType & {
  items?: Array<StylingWorkflowRunItemDTOType>;
  image_generation_model?: StylingWorkflowRunItemDTOType['image_generation_model'];
};

export const stylistWorkflowsApi = createApi({
  reducerPath: 'stylistWorkflowsApi',
  baseQuery: stylistBaseQuery,
  tagTypes: [...stylistTagTypes],
  endpoints: builder => ({
    getImageModels: builder.query<ImageModelListResponseType, { include_all?: boolean } | void>({
      query: args => {
        const searchParams = new URLSearchParams();
        if (args?.include_all) {
          searchParams.append('include_all', 'true');
        }
        const suffix = searchParams.toString();
        return {
          url: `/stylist/image-models/${suffix ? `?${suffix}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['StylingWorkflow'],
      extraOptions: { zodSchema: ImageModelListResponseSchema },
    }),

    getOccasions: builder.query<OccasionListResponseType, void>({
      query: () => ({
        url: '/stylist/occasions/',
        method: 'GET',
      }),
      providesTags: ['Occasion'],
      extraOptions: { zodSchema: OccasionListResponseSchema },
    }),

    getOccasionById: builder.query<OccasionDTOType, number>({
      query: id => ({
        url: `/stylist/occasions/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Occasion', id }],
    }),

    createOccasion: builder.mutation<OccasionDTOType, OccasionCreateRequestType>({
      query: body => ({
        url: '/stylist/occasions/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Occasion'],
    }),

    updateOccasion: builder.mutation<
      OccasionDTOType,
      { id: number; data: Partial<OccasionCreateRequestType> }
    >({
      query: ({ id, data }) => ({
        url: `/stylist/occasions/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Occasion'],
    }),

    moveOccasion: builder.mutation<
      OccasionDTOType,
      {
        id: number;
        data: {
          parent_id?: number | null;
          before_id?: number;
          after_id?: number;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/stylist/occasions/${id}/move/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Occasion'],
    }),

    deleteOccasion: builder.mutation<void, number>({
      query: id => ({
        url: `/stylist/occasions/${id}/`,
        method: 'DELETE',
        responseHandler: 'text',
      }),
      invalidatesTags: ['Occasion'],
    }),

    getStylingWorkflows: builder.infiniteQuery<
      StylingWorkflowListResponseType,
      {
        filters?: Array<FilterDTOType>;
        workflow_genre?: 'standard' | 'occasion';
      },
      {
        page: number;
        size: number;
        filters: Array<FilterDTOType>;
        workflow_genre?: 'standard' | 'occasion';
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          page: 1,
          size: 10,
          filters: [],
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.results.length === 0) {
            return undefined;
          }

          if (lastPage.results.length < lastPageParam.size) {
            return undefined;
          }

          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
            filters: lastPageParam.filters,
          };
        },
      },
      query: ({ pageParam, queryArg }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('page', pageParam.page.toString());
        searchParams.append('page_size', pageParam.size.toString());
        if (queryArg.workflow_genre) {
          searchParams.append('workflow_genre', queryArg.workflow_genre);
        }

        (queryArg.filters ?? []).forEach(filter => {
          searchParams.append(filter.field, filter.value);
        });

        return {
          url: `/stylist/workflows/?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['StylingWorkflow'],
    }),

    getStylingWorkflowById: builder.query<StylingWorkflowDTOType, number>({
      query: id => ({
        url: `/stylist/workflows/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'StylingWorkflow', id }],
    }),

    createStylingWorkflow: builder.mutation<StylingWorkflowDTOType, CreateStylingWorkflowPayload>({
      query: workflow => {
        const workflowPayload = {
          ...workflow,
        };
        delete workflowPayload.items;
        delete workflowPayload.image_generation_model;
        return {
          url: `/stylist/workflows/`,
          method: 'POST',
          body: workflowPayload,
        };
      },
    }),

    updateStylingWorkflow: builder.mutation<
      StylingWorkflowDTOType,
      { id: number; data: Partial<StylingWorkflowDTOType> }
    >({
      query: ({ id, data }) => ({
        url: `/stylist/workflows/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'StylingWorkflow', id }],
    }),

    deleteStylingWorkflow: builder.mutation<void, number>({
      query: id => ({
        url: `/stylist/workflows/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'StylingWorkflow', id }],
    }),

    startWorkflowRun: builder.mutation<
      StylingWorkflowDTOType,
      {
        workflowId: number;
        items: Array<StylingWorkflowRunItemDTOType>;
      }
    >({
      query: ({ workflowId, items }) => ({
        url: `/stylist/workflows/${workflowId}/start-run/`,
        method: 'POST',
        body: { items },
      }),
      invalidatesTags: ['StylingWorkflow'],
    }),

    reRunFailedWorkflowTasks: builder.mutation<
      StylingWorkflowDTOType,
      {
        workflowId: number;
      }
    >({
      query: ({ workflowId }) => ({
        url: `/stylist/workflows/${workflowId}/re-run/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['StylingWorkflow'],
    }),
  }),
});

export const {
  useGetImageModelsQuery,
  useGetOccasionsQuery,
  useGetOccasionByIdQuery,
  useLazyGetOccasionByIdQuery,
  useCreateOccasionMutation,
  useUpdateOccasionMutation,
  useMoveOccasionMutation,
  useDeleteOccasionMutation,
  useGetStylingWorkflowsInfiniteQuery,
  useGetStylingWorkflowByIdQuery,
  useCreateStylingWorkflowMutation,
  useUpdateStylingWorkflowMutation,
  useDeleteStylingWorkflowMutation,
  useStartWorkflowRunMutation,
  useReRunFailedWorkflowTasksMutation,
} = stylistWorkflowsApi;
